import { Injectable } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions'
import { collection, doc, docData, Firestore, getDocs, limit, query, where } from '@angular/fire/firestore'
import { FirestoreStructure, Affiliate, AffiliateAdminRow, AffiliateDashboard, AffiliatePayoutDetails, AffiliateReferralType, AffiliateStatus, AffiliateKind, AffiliateBountyStatus, AffiliateBountyPaymentMethod } from '@parkupp/core'
import { Observable } from 'rxjs'
import { CampaignTrackingService } from './campaign-tracking.service'
import { GlobalService } from './global.service'

const VISITOR_COOKIE_NAME = 'parkupp_visitor_id'

export interface AffiliateSaveInput {
    /** Omit when creating a security guard */
    uid?: string
    /** Creating only: lets the server resolve the login account when uid is a CRM lead record */
    email?: string | null
    code?: string | null
    status?: AffiliateStatus
    parkerCommissionPct?: number | null
    landlordCommissionPct?: number | null
    parkerWindowMonths?: number | null
    landlordWindowMonths?: number | null
    notes?: string | null
    kind?: AffiliateKind
    /** Security guards only */
    displayName?: string | null
    phone?: string | null
    guardEmail?: string | null
    bountyAmount?: number | null
    bountyPaidInvoices?: number | null
}

export interface AffiliateBountyRow {
    id: string
    affiliateUid: string
    affiliateName: string | null
    affiliateCode: string | null
    affiliatePhone: string | null
    referralId: string
    clientKey: string
    denormClientName: string | null
    amount: number
    paidInvoicesAtQualifying: number
    threshold: number
    status: AffiliateBountyStatus
    qualifiedAt: string
    paidAt: string | null
    paymentMethod: AffiliateBountyPaymentMethod | null
    reference: string | null
    notes: string | null
}

export interface AffiliateAttributionView {
    id: string
    affiliateUid: string
    affiliateName: string
    status: string
    source: string
    startsAt: string | null
    endsAt: string | null
    createdAt: string
}

export interface AffiliateBuildingRow {
    parkingKey: string
    title: string
    parkingStatus: string | null
    firstListedAt: string | null
    createdAt: string
    referral: AffiliateAttributionView | null
}

export interface AffiliateLandlordRow {
    merchantKey: string
    companyName: string
    createdAt: string
    denormEnterpriseKey: string
    commissionPercentage: number | null
    /** ParkUpp fee % the fee share maths uses (unset → 15, explicit null → 0) */
    effectivePlatformPct: number
    introduction: AffiliateAttributionView | null
    buildings: AffiliateBuildingRow[]
}

@Injectable({ providedIn: 'root' })
export class AffiliateService {
    private claimAttempted = false

    constructor(private functions: Functions, private firestore: Firestore, private campaignTrackingService: CampaignTrackingService, private globalService: GlobalService) {}

    private call<T>(name: string, data: any = {}, timeout?: number): Promise<T> {
        return httpsCallable<any, T>(this.functions, name, timeout ? { timeout } : undefined)(data).then((r) => r.data)
    }

    /** affiliates/{uid}: readable by its owner; drives the client menu item. */
    watchAffiliate(uid: string): Observable<Affiliate | undefined> {
        return docData(doc(this.firestore, 'affiliates', uid)) as Observable<Affiliate | undefined>
    }

    /**
     * Find a person's clientsAdmin record by email. One email can have several records (CRM leads have
     * random ids and no login), so prefer the one created from a login account, then one with a name.
     * The server re-resolves the login account by email anyway; this just shows the right name.
     */
    async findAccountByEmail(email: string): Promise<{ uid: string; name: string; email: string; data: any } | null> {
        const ref = collection(this.firestore, FirestoreStructure.ADMIN_CLIENTS)
        const variants = Array.from(new Set([email.trim(), email.trim().toLowerCase()]))
        const docs = (await Promise.all(variants.map((e) => getDocs(query(ref, where('email', '==', e), limit(25)))))).flatMap((s) => s.docs)
        if (!docs.length) return null
        const score = (d: any) => (d.data().authUserCreatedAt || d.data().noAuth === false ? 2 : 0) + (d.data().firstName ? 1 : 0)
        const best = [...docs].sort((a, b) => score(b) - score(a))[0]
        const data: any = best.data()
        return { uid: best.id, name: [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || '(no name yet)', email: data.email || email.trim(), data }
    }

    /** Count a click when the page was opened from an affiliate link (?aff=CODE). */
    trackLandingClick(): void {
        const code = this.campaignTrackingService.landingAffiliateCode
        const visitorId = this.globalService.getCookie(VISITOR_COOKIE_NAME)
        if (!code || !visitorId) return
        this.call('affiliateTrackClick', { code, visitorId, landingPath: window.location.pathname, referrer: document.referrer || null }).catch((error) =>
            console.warn('[Affiliate] Click tracking failed:', error?.message)
        )
    }

    /** After sign-in: bind this account to the affiliate in the cookie. The server decides eligibility. */
    claimReferralIfReferred(): void {
        const code = this.campaignTrackingService.getAffiliateCode()
        if (!code || this.claimAttempted) return
        this.claimAttempted = true
        this.call<{ claimed: boolean; reason?: string }>('affiliateClaimReferral', { code })
            .then((result) => console.log('[Affiliate] Referral claim:', result))
            .catch((error) => console.warn('[Affiliate] Referral claim failed:', error?.message))
    }

    // ---- Affiliate (client) ----
    getDashboard(): Promise<AffiliateDashboard | null> {
        return this.call('affiliateGetDashboard')
    }
    savePayoutDetails(details: Partial<AffiliatePayoutDetails>): Promise<void> {
        return this.call('affiliateSavePayoutDetails', details)
    }

    /** Shareable links for an affiliate code. */
    links(code: string): { parker: string; landlord: string } {
        const origin = window.location.origin
        return { parker: `${origin}/?aff=${encodeURIComponent(code)}`, landlord: `${origin}/landlord?aff=${encodeURIComponent(code)}` }
    }

    // ---- Admin ----
    adminSave(input: AffiliateSaveInput): Promise<{ uid: string; code: string }> {
        return this.call('affiliateAdminSave', input)
    }
    adminList(): Promise<AffiliateAdminRow[]> {
        return this.call('affiliateAdminList')
    }
    adminDetail(uid: string): Promise<AffiliateDashboard & { clicksByDay: { day: string; clicks: number }[]; commissionsRaw: any[] }> {
        return this.call('affiliateAdminDetail', { uid })
    }
    adminAttachReferral(input: {
        type: AffiliateReferralType
        affiliateUid: string
        referredUid?: string
        referredEmail?: string
        merchantKey?: string
        parkingKey?: string
        /** Building only: first listing date (ISO), if different from the recorded one */
        listedAt?: string | null
        notes?: string
        replace?: boolean
    }): Promise<{ id: string }> {
        return this.call('affiliateAdminAttachReferral', input)
    }
    adminRevokeReferral(referralId: string, notes?: string): Promise<void> {
        return this.call('affiliateAdminRevokeReferral', { referralId, notes })
    }
    adminLandlords(): Promise<AffiliateLandlordRow[]> {
        return this.call('affiliateAdminLandlords')
    }
    adminReport(from: string, to: string): Promise<{ affiliates: Record<string, { displayName: string; code: string }>; commissions: any[] }> {
        return this.call('affiliateAdminReport', { from, to })
    }
    adminPayoutRequests(): Promise<any[]> {
        return this.call('affiliateAdminPayoutRequests')
    }
    adminPayoutAction(requestId: string, action: 'approve' | 'pay' | 'reject', extra: { reference?: string; reason?: string } = {}): Promise<void> {
        return this.call('affiliateAdminPayoutAction', { requestId, action, ...extra })
    }
    adminGenerateStatements(period?: string): Promise<{ period: string; approved: number; statements: number; total: number; carriedForward: number }> {
        return this.call('affiliateAdminGenerateStatements', { period }, 540000)
    }
    adminIntegrityCheck(from: string, fix: boolean): Promise<{ checkedInvoices: number; missing: string[]; stale: string[]; doubleEarned: string[]; fixed: number; bountiesCreated?: number }> {
        return this.call('affiliateAdminIntegrityCheck', { from, fix }, 540000)
    }
    adminBounties(): Promise<AffiliateBountyRow[]> {
        return this.call('affiliateAdminBounties')
    }
    adminBountyAction(bountyId: string, action: 'pay' | 'cancel' | 'reopen', extra: { method?: AffiliateBountyPaymentMethod; reference?: string; notes?: string } = {}): Promise<void> {
        return this.call('affiliateAdminBountyAction', { bountyId, action, ...extra })
    }
}
